import { useEffect, useState } from 'react';
import { Download, FileText } from 'lucide-react';
import { qd, apiError } from '../api';
import { useQdAuth } from '../QdApp';

const EMPTY = { title: '', category: 'Datasheet', language: 'en', version: '1.0', url: '', visibility: 'internal', published: true };
const CATS = ['Datasheet', 'Gateway datasheet', 'Sensor datasheet', 'Integration guide', 'Sales presentation', 'Other'];

export default function QdDocuments() {
  const { user } = useQdAuth();
  const isAdmin = ['admin', 'superadmin'].includes(user.role);
  const [docs, setDocs] = useState([]);
  const [form, setForm] = useState(null);
  const [search, setSearch] = useState('');
  const [msg, setMsg] = useState('');

  const load = () => qd.get('/documents').then((r) => setDocs(r.data));
  useEffect(() => { load(); }, []);

  const save = async () => {
    setMsg('');
    try {
      if (form.id) await qd.patch(`/documents/${form.id}`, form);
      else await qd.post('/documents', form);
      setForm(null); load();
    } catch (e) { setMsg(apiError(e)); }
  };

  const shown = docs.filter((d) => d.title.toLowerCase().includes(search.toLowerCase()));

  return (
    <div className="p-6 md:p-8">
      <div className="flex items-center justify-between mb-2 flex-wrap gap-3">
        <h1 className="text-xl font-bold text-[#14233F]">Documentation</h1>
        <div className="flex gap-2">
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search…" className="border border-slate-300 rounded px-3 py-2 text-sm" data-testid="qd-doc-search" />
          {isAdmin && <button onClick={() => setForm({ ...EMPTY })} className="bg-[#0F7D70] text-white px-4 py-2 rounded text-sm font-semibold" data-testid="qd-new-doc-btn">Add document</button>}
        </div>
      </div>
      <p className="text-xs text-slate-400 mb-6">Upload files (max 25 MB) — stored securely in Supabase Storage — or link an external URL.</p>

      {form && isAdmin && (
        <div className="bg-white border border-slate-200 rounded p-5 mb-6 grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="qd-doc-form">
          <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Title *" className="border border-slate-300 rounded px-3 py-2 text-sm md:col-span-2" data-testid="qd-doc-title" />
          <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="border border-slate-300 rounded px-3 py-2 text-sm">
            {CATS.map((c) => <option key={c}>{c}</option>)}
          </select>
          <input value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} placeholder="External URL (optional if uploading a file)" className="border border-slate-300 rounded px-3 py-2 text-sm md:col-span-2 mono text-xs" data-testid="qd-doc-url" />
          {!form.id && (
            <input type="file" onChange={(e) => setForm({ ...form, _file: e.target.files[0] })}
              className="border border-slate-300 rounded px-3 py-2 text-xs md:col-span-3" data-testid="qd-doc-file" />
          )}
          <div className="flex gap-2 items-center">
            <input value={form.version} onChange={(e) => setForm({ ...form, version: e.target.value })} placeholder="v" className="border border-slate-300 rounded px-2 py-2 text-sm w-16 mono" />
            <select value={form.visibility} onChange={(e) => setForm({ ...form, visibility: e.target.value })} className="border border-slate-300 rounded px-2 py-2 text-sm">
              <option value="internal">Internal</option><option value="shareable">Shareable</option>
            </select>
            <label className="text-xs text-slate-500 flex items-center gap-1">
              <input type="checkbox" checked={form.published} onChange={(e) => setForm({ ...form, published: e.target.checked })} /> Published
            </label>
          </div>
          <div className="flex gap-2 md:col-span-3">
            <button onClick={save} className="bg-[#14233F] text-white px-4 py-2 rounded text-sm" data-testid="qd-doc-save">Save</button>
            <button onClick={() => setForm(null)} className="border border-slate-300 px-4 py-2 rounded text-sm text-slate-600">Cancel</button>
            {msg && <span className="text-xs text-red-600 self-center">{msg}</span>}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {shown.map((d) => (
          <div key={d.id} className={`bg-white border border-slate-200 rounded p-5 ${!d.published ? 'opacity-50' : ''}`} data-testid={`qd-doc-card-${d.id}`}>
            <div className="flex items-start justify-between mb-2">
              <FileText className="h-5 w-5 text-[#0F7D70]" />
              <span className={`text-[10px] px-1.5 py-0.5 rounded ${d.visibility === 'shareable' ? 'bg-teal-50 text-teal-700' : 'bg-slate-100 text-slate-500'}`}>{d.visibility}</span>
            </div>
            <p className="font-semibold text-sm text-slate-800 mb-1">{d.title}</p>
            <p className="text-[11px] text-slate-400 mb-3">{d.category} · {d.language.toUpperCase()} · v{d.version} · {d.updated_at?.slice(0, 10)}</p>
            <div className="flex gap-3">
              {d.url && (
                <a href={d.url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-xs font-semibold text-[#0F7D70] hover:underline">
                  <Download className="h-3.5 w-3.5" /> Download
                </a>
              )}
              {isAdmin && <button onClick={() => setForm(d)} className="text-xs text-slate-400 hover:text-slate-700">Edit</button>}
            </div>
          </div>
        ))}
        {shown.length === 0 && <p className="text-sm text-slate-400 col-span-3">No documents yet.</p>}
      </div>
    </div>
  );
}
